<template>
  <div class="bg-[#0a0a33] min-h-screen mt-9 font-sans antialiased text-white">
    
    <section class="relative w-full h-[90vh] overflow-hidden flex items-center">
      <div class="absolute inset-0 z-0">
        <transition name="apple-fade">
          <img
            :key="currentImage"
            :src="images[currentImage]"
            class="absolute inset-0 w-full h-full object-cover scale-105"
            alt="Background"
          />
        </transition>
        <div class="absolute inset-0 bg-gradient-to-r from-[#0a0a33] via-[#0a0a33]/60 to-transparent"></div>
      </div>

      <div class="relative z-10 px-6 md:px-16 lg:px-24 max-w-4xl" data-aos="fade-right">
        <div :class="['transition-all duration-1000 ease-out', textAnimation]">
          <span class="inline-block px-3 py-1 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-400 text-xs font-semibold tracking-widest uppercase mb-6">
            {{ hero.tagline }}
          </span>

          <h1 class="text-4xl md:text-7xl font-bold tracking-tight leading-[1.1] mb-6">
            {{ hero.title_prefix }} <br />
            <span class="text-transparent bg-clip-text bg-gradient-to-b from-blue-300 to-blue-600">
               {{ hero.center }}
            </span>
          </h1>

          <p class="text-lg md:text-xl text-gray-300/90 max-w-xl mb-10 leading-relaxed font-light">
            {{ hero.description }}
          </p>

          <div class="flex flex-col sm:flex-row items-center gap-5">
            <button @click="returnFormation" class="w-full sm:w-auto bg-blue-600 hover:bg-blue-500 text-white px-10 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-2xl shadow-blue-600/20 active:scale-95">
              {{ hero.cta }}
            </button>
            <router-link :to="{name: 'ContactView'}" class="flex items-center gap-2 text-white/80 hover:text-white font-medium group">
              {{ hero.contact }}
              <span class="group-hover:translate-x-1 transition-transform">→</span>
            </router-link>
          </div>
        </div>
      </div>

      <div class="absolute bottom-10 right-10 flex gap-2">
        <div 
          v-for="(_, index) in images" 
          :key="index"
          @click="currentImage = index"
          :class="['h-1.5 rounded-full transition-all duration-500 cursor-pointer', currentImage === index ? 'w-8 bg-blue-500' : 'w-2 bg-white/20']"
        ></div>
      </div>
    </section>

    <section class="py-24 px-6 md:px-16 lg:px-24 bg-white/[0.02] border-y border-white/5">
      <div class="max-w-7xl mx-auto">
        <div class="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-4">
          <div data-aos="fade-up">
            <h2 class="text-sm font-semibold text-blue-400 uppercase tracking-widest mb-2">Témoignages</h2>
            <p class="text-3xl md:text-4xl font-bold tracking-tight">Ce que disent nos apprenants.</p>
          </div>
          <p class="text-gray-400 max-w-xs text-sm font-light">Rejoignez des centaines d'étudiants qui ont réussi leur projet avec NovaLingua.</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div 
            v-for="(avis, index) in commentaires" 
            :key="index"
            class="p-8 rounded-[2rem] bg-white/[0.03] border border-white/10 backdrop-blur-md hover:bg-white/[0.06] transition-all duration-500 group"
            data-aos="fade-up"
            :data-aos-delay="index * 100"
          >
            <div class="flex gap-1 mb-6">
              <span v-for="i in 5" :key="i" class="text-blue-400 text-xs">★</span>
            </div>
            
            <p class="text-gray-200 mb-8 italic font-light leading-relaxed">
              "{{ avis.texte }}"
            </p>

            <div class="flex items-center gap-4">
              <div class="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center font-bold text-sm text-white border-2 border-white/10">
                {{ avis.nom.charAt(0) }}
              </div>
              <div>
                <h4 class="font-semibold text-white">{{ avis.nom }}</h4>
                <p class="text-xs text-gray-500 uppercase tracking-tighter">{{ avis.status }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import AOS from "aos";
import "aos/dist/aos.css";

// Vos images existantes
import etudiant from "@/assets/images/etudiant.png";
import salle_fete from "@/assets/images/salle_fete.png";
import lang from "@/assets/images/lang.png";
import heureux from "@/assets/images/heureux.png";
import apprentissage from "@/assets/images/apprentissage.png";
import router from "@/router";

const images = [etudiant, salle_fete, lang, heureux, apprentissage];
const currentImage = ref(0);
const showText = ref(true);
let interval;

const hero = ref({
  tagline: "Apprenez les langues autrement",
  title_prefix: "Bienvenue au",
  center: "Centre NovaLingua",
  description: "Un espace moderne et dynamique à Bafoussam, où chaque apprenant découvre le plaisir de maîtriser l’allemand, l’anglais, le français ou l’italien. Nos formations sont assurées par des enseignants qualifiés.",
  cta: "Découvrir nos formations",
  contact: "Nous contacter",
});

// Nouveaux commentaires
const commentaires = ref([
  {
    nom: "Marc Etoundi",
    status: "Étudiant en Allemagne",
    texte: "Grâce à NovaLingua, j'ai obtenu mon certificat B2 en un temps record. L'accompagnement pour mon visa était impeccable."
  },
  {
    nom: "Sophie Kamga",
    status: "Apprenante Niveau B1",
    texte: "Les enseignants sont très pédagogues. L'ambiance est conviviale et propice à l'apprentissage rapide des langues."
  },
  {
    nom: "Jean-Paul T.",
    status: "Professionnel",
    texte: "Un centre moderne avec des outils digitaux qui facilitent vraiment la compréhension de l'allemand. Je recommande !"
  },
  { 
    nom: "Marc Etoundi", 
    texte: "Grâce à l'Institut, j'ai obtenu mon certificat B2 en un temps record. L'accompagnement pour mon visa était impeccable.",
    status: "Étudiant en Allemagne"
  },
  { 
    nom: "Sophie Kamga", 
    texte: "Les enseignants sont très pédagogues. L'ambiance est conviviale et propice à l'apprentissage rapide des langues.",
    status: "Apprenante Niveau B1"
  },

  { 
    nom: "Sandra Belinga", 
    texte: "La formation en Secrétariat Bureautique m'a permis de maîtriser Excel à un niveau expert. J'ai trouvé un emploi deux semaines après ma certification.",
    status: "Assistante de Direction"
  },
]);

const textAnimation = computed(() =>
  showText.value ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
);
const returnFormation = ()=>{
  router.push('/formations')
}
const startSlider = () => {
  interval = setInterval(() => {
    showText.value = false;
    setTimeout(() => {
      currentImage.value = (currentImage.value + 1) % images.length;
      showText.value = true;
    }, 700);
  }, 6000);
};

onMounted(() => {
  AOS.init({ once: true });
  startSlider();
});

onBeforeUnmount(() => clearInterval(interval));
</script>

<style scoped>
/* Transition douce Apple */
.apple-fade-enter-active,
.apple-fade-leave-active {
  transition: opacity 1.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.apple-fade-enter-from,
.apple-fade-leave-to {
  opacity: 0;
}

/* Typographie SF Pro Style */
h1, h2, h3 {
  letter-spacing: -0.02em;
}

/* Effet de brillance subtile sur les cartes */
.group:hover {
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  transform: translateY(-5px);
}
</style>